import React from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Sparkles, Wheat, Building2, ArrowUpRight } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { SectionHeading } from "../motion/SectionHeading";
import { Stagger, StaggerItem, Reveal } from "../motion/Reveal";

const ICONS = [TrendingUp, Sparkles, Wheat, Building2];

export const FinancialBenefits = () => {
  const { t } = useLang();
  return (
    <section className="py-10 sm:py-14 bg-white" data-testid="financial-section">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <SectionHeading overline={t.financial.overline} title={t.financial.title} />
            <Reveal delay={0.2}>
              <div className="mt-8 rounded-2xl overflow-hidden hover-zoom">
                <img
                  src="https://images.unsplash.com/photo-1560493676-04071c5f467b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                  alt="Vineyard investment"
                  className="w-full h-64 object-cover"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Stagger className="grid sm:grid-cols-2 gap-5">
              {t.financial.items.map((item, i) => {
                const Icon = ICONS[i];
                return (
                  <StaggerItem key={i}>
                    <div className="group bg-lightgray rounded-2xl p-7 h-full border border-transparent hover:border-gold/30 transition-all duration-500" data-testid={`financial-card-${i}`}>
                      <span className="w-11 h-11 rounded-xl bg-white flex items-center justify-center mb-5 shadow-sm">
                        <Icon className="w-5 h-5 text-green" />
                      </span>
                      <h3 className="font-display text-lg text-charcoal mb-2">{item.t}</h3>
                      <p className="text-charcoal/55 text-sm leading-relaxed">{item.d}</p>
                    </div>
                  </StaggerItem>
                );
              })}
            </Stagger>
          </div>
        </div>
      </div>
    </section>
  );
};

export const CTASection = () => {
  const { t } = useLang();
  return (
    <section className="py-10 sm:py-14 bg-green relative overflow-hidden grain" data-testid="cta-section">
      <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl" />
      <div className="max-w-[1000px] mx-auto px-5 sm:px-8 text-center relative">
        <Reveal>
          <span className="overline">{t.contact.overline}</span>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight mt-5 leading-[1.05]">
            {t.contact.title}
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="text-white/70 text-lg mt-6 max-w-xl mx-auto">{t.contact.sub}</p>
        </Reveal>
        <Reveal delay={0.26}>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/contact" data-testid="cta-book-btn" className="btn-shine group inline-flex items-center gap-2 rounded-full bg-gold text-charcoal font-semibold px-8 py-4 hover:bg-gold-soft transition-colors">
              {t.cta.book}
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/projects" data-testid="cta-explore-btn" className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white font-medium px-8 py-4 hover:bg-white/10 transition-colors">
              {t.cta.explore}
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
